// src/components/layout/ResponsiveDrawer.tsx
import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  Menu,
  BarChart3,
  BookOpen,
  Activity,
  User,
  GraduationCap,
  LogOut,
  Users,
  Lock,
  Moon,
  Sun,
  NotebookPen,
  HelpCircle,
  HeartPulse,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  Search,
  Library,
  Route,
  Award,
  Star,
  ChevronLeft,
} from "lucide-react";

// Auth & Firestore
import { observeAuth, logout } from "@/firebase/auth.custom";
import { db } from "@/firebase/firebase-config";
import { doc, onSnapshot } from "firebase/firestore";
import { usePlan } from "@/hooks/usePlan";
import ZillHubLogoBlue_noBG from "@/assets/ZillHubLogoBlue_noBG.png";
import ZillHubLogoBlack_noBG from "@/assets/ZillHubLogoBlack_noBG.png";
import { useTheme } from "@/context/ThemeContext";
import { getFriendsSummary } from "@/services/friendsService";

// Global cache to prevent redundant API calls across component remounts
let friendSummaryCache: { data: number; timestamp: number; userId: string } | null = null;
const CACHE_DURATION = 30000; // 30 seconds
let pendingFetchPromise: Promise<void> | null = null;

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: BarChart3, tutorialId: "nav-dashboard" },
  { name: "Your Journal", href: "/journal", icon: NotebookPen, tutorialId: "nav-journal" },
  { name: "Resiliency Status", href: "/resilience-status", icon: HeartPulse, tutorialId: "nav-resilience" },
  { name: "Learning Center", href: "/learning", icon: BookOpen, tutorialId: "nav-learning" },
  { name: "Activity", href: "/activity", icon: Activity, tutorialId: "nav-activity" },
  { name: "Friends", href: "/friends", icon: Users, tutorialId: "nav-friends" },
  { name: "Accomplishments", href: "/accomplishments", icon: Award, tutorialId: "nav-accomplishments" }
];

// Route to page title mapping for mobile header
const getPageTitle = (pathname: string): string => {
  // Exact matches first
  const exactMatch = navigation.find(item => item.href === pathname);
  if (exactMatch) return exactMatch.name;

  // Partial matches for nested routes
  if (pathname.startsWith('/learning')) return 'Learning Center';
  if (pathname.startsWith('/journal')) return 'Your Journal';
  if (pathname.startsWith('/resilience-status')) return 'Resiliency Status';
  if (pathname.startsWith('/activity')) return 'Activity';
  if (pathname.startsWith('/friends')) return 'Friends';
  if (pathname.startsWith('/accomplishments')) return 'Accomplishments';
  if (pathname.startsWith('/dashboard')) return 'Dashboard';
  if (pathname.startsWith('/family-dashboard')) return 'Family Dashboard';
  if (pathname.startsWith('/profile')) return 'Profile';
  if (pathname.startsWith('/admin')) return 'Admin';
  if (pathname.startsWith('/coach')) return 'Coach';
  if (pathname.startsWith('/lesson')) return 'Lesson';
  
  return 'ZillHub';
};

interface ResponsiveDrawerProps {
  children: React.ReactNode;
}

export default function ResponsiveDrawer({ children }: ResponsiveDrawerProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isGuest } = usePlan();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [learningCenterExpanded, setLearningCenterExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  };

  // Auth user (Firebase user object) and profile (Firestore /users/{uid})
  const [user, setUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [pendingFriendRequests, setPendingFriendRequests] = useState(0);

  // Keep refs to unsubscribe functions so we always clean up
  const authUnsubRef = useRef<null | (() => void)>(null);
  const userDocUnsubRef = useRef<null | (() => void)>(null);
  const authAttachTimer = useRef<any>(null);

  useEffect(() => {
    // Start observing auth
    const stopAuth = observeAuth((authUser) => {
      // Always clear any pending timer and previous doc listener
      if (authAttachTimer.current) {
        clearTimeout(authAttachTimer.current);
        authAttachTimer.current = null;
      }
      if (userDocUnsubRef.current) {
        userDocUnsubRef.current();
        userDocUnsubRef.current = null;
      }

      setUser(authUser);

      if (!authUser?.uid) {
        // Signed out → clear profile and don't attach any listeners
        setUserProfile(null);
        setPendingFriendRequests(0);
        return;
      }

      // Small debounce helps avoid race during credential swap
      authAttachTimer.current = setTimeout(() => {
        const ref = doc(db(), "users", authUser.uid);

        // Attach a safe listener to the current user's doc
        const unsub = onSnapshot(
          ref,
          (snap) => {
            setUserProfile(snap.exists() ? snap.data() : null);
          },
          (err) => {
            // During logout/login, Firestore may briefly report permission-denied.
            if ((err as any)?.code === "permission-denied") {
              console.warn("[drawer] transient permission change on auth swap");
              setUserProfile(null);
            } else {
              console.error("[drawer] user doc listener error:", err);
            }
          }
        );

        userDocUnsubRef.current = unsub;
      }, 50);
    });

    authUnsubRef.current = stopAuth;

    // Cleanup on unmount
    return () => {
      if (authAttachTimer.current) clearTimeout(authAttachTimer.current);
      if (userDocUnsubRef.current) userDocUnsubRef.current();
      if (authUnsubRef.current) authUnsubRef.current();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let interval: NodeJS.Timeout;

    if (!user?.uid) {
      setPendingFriendRequests(0);
      return;
    }

    const loadFriendSummary = async () => {
      if (cancelled) return;

      // Check if cache is still valid AND belongs to current user
      const now = Date.now();
      if (
        friendSummaryCache && 
        friendSummaryCache.userId === user.uid &&
        now - friendSummaryCache.timestamp < CACHE_DURATION
      ) {
        setPendingFriendRequests(friendSummaryCache.data);
        return;
      }

      // If there's already a pending fetch for a different user, clear it
      if (friendSummaryCache && friendSummaryCache.userId !== user.uid) {
        friendSummaryCache = null;
        pendingFetchPromise = null;
      }

      // If there's already a pending fetch, wait for it
      if (pendingFetchPromise) {
        try {
          await pendingFetchPromise;
          if (friendSummaryCache && friendSummaryCache.userId === user.uid && !cancelled) {
            setPendingFriendRequests(friendSummaryCache.data);
          }
        } catch (err) {
          // Error already logged by the pending fetch
        }
        return;
      }

      // Start new fetch
      pendingFetchPromise = (async () => {
        try {
          const res = await getFriendsSummary(user.uid);
          const count = res.incomingRequests?.length ?? 0;
          
          // Update cache with user ID
          friendSummaryCache = {
            data: count,
            timestamp: Date.now(),
            userId: user.uid
          };
          
          if (!cancelled) {
            setPendingFriendRequests(count);
          }
        } catch (err) {
          console.error("Failed to load friend summary", err);
        } finally {
          pendingFetchPromise = null;
        }
      })();

      await pendingFetchPromise;
    };

    // Listen for friend request updates from other components
    const handleFriendRequestUpdate = () => {
      // Invalidate cache and reload
      friendSummaryCache = null;
      loadFriendSummary();
    };

    window.addEventListener('friendRequestsUpdated', handleFriendRequestUpdate);

    // Load immediately (will use cache if available)
    loadFriendSummary();
    
    // Refresh every 60 seconds
    interval = setInterval(loadFriendSummary, 60000);

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      window.removeEventListener('friendRequestsUpdated', handleFriendRequestUpdate);
    };
  }, [user?.uid]);

  const handleLogout = async () => {
    try {
      // Proactively tear down user doc listener before logging out
      if (userDocUnsubRef.current) {
        userDocUnsubRef.current();
        userDocUnsubRef.current = null;
      }
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const Logo = () => (
    <div className={cn(
      "flex items-center py-4",
      isCollapsed ? "justify-center px-0" : "justify-center px-2"
    )}>
      {isCollapsed ? (
        <img
          src={isDark ? ZillHubLogoBlack_noBG : ZillHubLogoBlue_noBG}
          alt="ZillHub"
          className="w-10 h-10 object-contain"
        />
      ) : (
        <div className={`w-56 h-40 border rounded-2xl shadow-lg flex flex-col items-center justify-center relative overflow-hidden ${
          isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"
        }`}>
          <img
            src={isDark ? ZillHubLogoBlack_noBG : ZillHubLogoBlue_noBG}
            alt="ZillHub Logo"
            className="w-full h-3/4 object-contain p-3"
          />
          <p className={`text-base font-medium ${
            isDark ? "text-white" : "text-black"
          }`}>Resilience Builder</p>
        </div>
      )}
    </div>
  );

  const Navigation = ({ collapsed = false }: { collapsed?: boolean }) => {
    const activeStyle = isDark
      ? "bg-slate-800 text-white border border-slate-700"
      : "bg-blue-50 text-blue-700 border border-blue-100";
    const idleStyle = isDark
      ? "text-slate-200 hover:bg-slate-800 hover:text-white"
      : "hover:bg-gray-50 text-gray-700 hover:text-gray-900";

    const baseExpanded = "w-full justify-start h-12 rounded-xl transition-all duration-200";
    const baseCollapsed = "w-full justify-center h-12 rounded-xl transition-all duration-200 px-0";

    return (
      <nav aria-label="Main navigation" className={cn("pb-4 space-y-1", collapsed ? "px-2" : "px-4")}>
        {navigation.map((item) => {
          const isActive =
            location.pathname === item.href ||
            location.pathname.startsWith(item.href);

          // Collapsed: icon-only with tooltip
          if (collapsed) {
            const hasBadge = item.href === "/friends" && pendingFriendRequests > 0;
            return (
              <Link key={item.name} to={item.href} title={item.name}>
                <Button
                  variant="ghost"
                  aria-label={item.name + (isActive ? " (current page)" : "")}
                  aria-current={isActive ? "page" : undefined}
                  data-tutorial={item.tutorialId}
                  className={cn(baseCollapsed, isActive ? activeStyle : idleStyle, "relative")}
                  onClick={() => setMobileOpen(false)}
                >
                  <item.icon className={cn("h-5 w-5", isActive && "text-primary")} aria-hidden="true" />
                  {hasBadge && (
                    <span aria-label={`${pendingFriendRequests} pending friend requests`} className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                      {pendingFriendRequests}
                    </span>
                  )}
                </Button>
              </Link>
            );
          }

          // Expanded: Learning Center special case
          if (item.name === "Learning Center") {
            return (
              <div key={item.name}>
                <Button
                  variant="ghost"
                  aria-expanded={learningCenterExpanded}
                  aria-controls="learning-submenu"
                  className={cn(baseExpanded, isActive ? activeStyle : idleStyle)}
                  onClick={() => setLearningCenterExpanded(!learningCenterExpanded)}
                >
                  <item.icon className={cn("mr-3 h-5 w-5", isActive && "text-primary")} aria-hidden="true" />
                  <span className="font-medium flex items-center gap-1">
                    {item.name}
                    {isGuest ? <Lock className="w-4 h-4 opacity-60" aria-label="Requires account" /> : null}
                  </span>
                  {learningCenterExpanded
                    ? <ChevronDown className="ml-auto h-4 w-4" aria-hidden="true" />
                    : <ChevronRight className="ml-auto h-4 w-4" aria-hidden="true" />}
                </Button>

                {learningCenterExpanded && (
                  <div id="learning-submenu" className={cn(
                    "ml-4 mt-1 space-y-1 py-2 px-2 rounded-lg border",
                    isDark ? "bg-slate-900/50 border-slate-800/50 shadow-inner" : "bg-gray-100/80 border-gray-200/50 shadow-inner"
                  )}>
                    {([
                      { label: "Search Lessons", icon: Search, href: "/learning?tab=browse", match: location.pathname === "/learning" && location.search.includes("tab=browse") },
                      { label: "My Lessons",     icon: Library, href: "/learning?tab=my-lessons", match: location.pathname === "/learning" && location.search.includes("tab=my-lessons") },
                      { label: "Learning Tracks",icon: Route,   href: "/tracks", match: location.pathname === "/tracks" },
                    ]).map(({ label, icon: Icon, href, match }) => (
                      <Button key={label} variant="ghost" aria-current={match ? "page" : undefined}
                        className={cn("w-full justify-start h-10 rounded-lg", match ? activeStyle : idleStyle)}
                        onClick={() => { navigate(href); setMobileOpen(false); }}
                      >
                        <Icon className="mr-3 h-4 w-4" aria-hidden="true" />
                        <span className="text-sm">{label}</span>
                      </Button>
                    ))}
                    {userProfile?.isChildAccount && (
                      <Button variant="ghost" aria-current={location.pathname === "/parent-curated" ? "page" : undefined}
                        className={cn("w-full justify-start h-10 rounded-lg", location.pathname === "/parent-curated" ? activeStyle : idleStyle)}
                        onClick={() => { navigate("/parent-curated"); setMobileOpen(false); }}
                      >
                        <Star className="mr-3 h-4 w-4" aria-hidden="true" />
                        <span className="text-sm">Parent Curated</span>
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          }

          // Expanded: standard nav item
          return (
            <Link key={item.name} to={item.href}>
              <Button
                variant="ghost"
                data-tutorial={item.tutorialId}
                aria-current={isActive ? "page" : undefined}
                className={cn(baseExpanded, isActive ? activeStyle : idleStyle)}
                onClick={() => setMobileOpen(false)}
              >
                <item.icon className={cn("mr-3 h-5 w-5", isActive && "text-primary")} aria-hidden="true" />
                <span className="font-medium flex items-center gap-1">
                  {item.name}
                  {item.href === "/friends" && pendingFriendRequests > 0 ? (
                    <span aria-label={`${pendingFriendRequests} pending friend requests`} className="ml-1 inline-flex items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                      {pendingFriendRequests}
                    </span>
                  ) : null}
                </span>
                {isActive && <div className="ml-auto w-2 h-2 bg-primary rounded-full" aria-hidden="true" />}
              </Button>
            </Link>
          );
        })}

        {/* Family Dashboard */}
        {userProfile?.isFamilyAccount && (
          collapsed ? (
            <Link to="/family-dashboard" title="Family Dashboard">
              <Button
                variant="ghost"
                aria-label={"Family Dashboard" + (location.pathname.startsWith("/family-dashboard") ? " (current page)" : "")}
                aria-current={location.pathname.startsWith("/family-dashboard") ? "page" : undefined}
                className={cn(baseCollapsed, "relative",
                  location.pathname.startsWith("/family-dashboard") ? activeStyle : idleStyle
                )}
                onClick={() => setMobileOpen(false)}
              >
                <Users className={cn("h-5 w-5", location.pathname.startsWith("/family-dashboard") && "text-primary")} aria-hidden="true" />
              </Button>
            </Link>
          ) : (
            <Link to="/family-dashboard">
              <Button
                variant="ghost"
                aria-current={location.pathname.startsWith("/family-dashboard") ? "page" : undefined}
                className={cn(
                  "w-full justify-start h-12 rounded-xl transition-all duration-200",
                  location.pathname.startsWith("/family-dashboard") ? activeStyle : idleStyle
                )}
                onClick={() => setMobileOpen(false)}
              >
                <Users className={cn("mr-3 h-5 w-5", location.pathname.startsWith("/family-dashboard") && "text-primary")} aria-hidden="true" />
                <span className="font-medium">Family Dashboard</span>
                {location.pathname.startsWith("/family-dashboard") && <div className="ml-auto w-2 h-2 bg-primary rounded-full" aria-hidden="true" />}
              </Button>
            </Link>
          )
        )}
      </nav>
    );
  };


  const UserProfileCard = () => {
    const initial =
      user?.displayName?.[0]?.toUpperCase() ||
      user?.email?.[0]?.toUpperCase() ||
      null;

    // Get photoURL from userProfile (Firestore) or user (Firebase Auth)
    const photoURL = userProfile?.photoURL || user?.photoURL || null;

    return (
      <div
        className={[
          "p-4 border-t",
          isDark ? "border-slate-800" : "border-gray-100",
        ].join(" ")}
      >
        <div
          className={[
            "rounded-xl p-4",
            isDark
              ? "bg-slate-900/80 border border-slate-800"
              : "bg-gradient-to-r from-gray-50 to-blue-50",
          ].join(" ")}
        >
          <div className="flex items-center">
            <Avatar className="w-12 h-12 ring-2 ring-primary/20">
              {photoURL && (
                <AvatarImage src={photoURL} alt="User avatar" />
              )}
              <AvatarFallback className="bg-gradient-to-r from-primary to-secondary text-white font-semibold">
                {initial ? initial : <User className="h-6 w-6" />}
              </AvatarFallback>
            </Avatar>
            <div className="ml-3 flex-1 min-w-0">
              <p
                className={[
                  "text-sm font-semibold truncate",
                  isDark ? "text-slate-50" : "text-gray-900",
                ].join(" ")}
              >
                {userProfile?.name || user?.displayName || "User"}
              </p>
              <p className={isDark ? "text-xs text-slate-300 truncate" : "text-xs text-gray-600 truncate"}>
                {user?.email}
              </p>
              <div className="flex items-center mt-1">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                <span className={isDark ? "text-xs text-slate-300" : "text-xs text-gray-500"}>
                  Online
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3 flex space-x-2">
            <Link to="/profile" className="flex-1">
              <Button variant="outline" size="sm" className="w-full text-xs">
                <User className="h-3 w-3 mr-1" />
                Profile
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex-1 text-xs"
            >
              <LogOut className="h-3 w-3 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const ThemeToggleCard = () => (
    <div className="px-4 pb-4">
      <div
        className={[
          "rounded-xl border p-4",
          isDark ? "border-slate-800 bg-slate-900/80" : "border-gray-200 bg-white",
        ].join(" ")}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className={isDark ? "text-sm font-semibold text-slate-100" : "text-sm font-semibold text-gray-900"}>
              Appearance
            </p>
            <p className={isDark ? "text-xs text-slate-300" : "text-xs text-gray-600"}>
              Switch between light and dark dashboards.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={isDark ? "outline" : "default"}
              size="icon"
              className={[
                "h-9 w-9",
                !isDark
                  ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                  : "border-slate-700 bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-100",
              ].join(" ")}
              onClick={() => setTheme("light")}
              aria-label="Switch to light mode"
            >
              <Sun className="h-4 w-4" />
            </Button>
            <Button
              variant={!isDark ? "outline" : "default"}
              size="icon"
              className={[
                "h-9 w-9",
                isDark
                  ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                  : "border-gray-200 bg-white text-slate-400 hover:bg-gray-50 hover:text-slate-800",
              ].join(" ")}
              onClick={() => setTheme("dark")}
              aria-label="Switch to dark mode"
            >
              <Moon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* Tutorial button - only show on dashboard */}
        {(location.pathname === '/' || location.pathname === '/dashboard') && (
          <Button
            variant="outline"
            size="sm"
            className={[
              "w-full",
              isDark
                ? "border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-700 hover:text-slate-100"
                : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100",
            ].join(" ")}
            onClick={() => {
              // Trigger tutorial - this will be connected via a prop or context
              const event = new CustomEvent('openTutorial');
              window.dispatchEvent(event);
            }}
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            View Tutorial
          </Button>
        )}
      </div>
    </div>
  );

  // CTA that appears right under the nav items
  const QuickQuizCTA = ({ collapsed = false }: { collapsed?: boolean }) => (
    <div className={cn("pb-4", collapsed ? "px-2" : "px-4")}>
      <Link to="/daily-challenge" title={collapsed ? "Daily Challenge" : undefined}>
        <Button
          onClick={() => setMobileOpen(false)}
          aria-label="Open Daily Challenge"
          className={cn(
            "w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-[0_8px_20px_rgba(79,70,229,0.25)] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
            collapsed && "justify-center px-0"
          )}
        >
          <GraduationCap className={cn("h-5 w-5", !collapsed && "mr-3")} aria-hidden="true" />
          {!collapsed && "Daily Challenge"}
        </Button>
      </Link>
    </div>
  );

  return (
    <>
      {/* Skip navigation - ADA/WCAG 2.1 AA */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-indigo-600 focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold focus:shadow-lg focus:ring-2 focus:ring-indigo-500"
      >
        Skip to main content
      </a>

      {/* Desktop Sidebar */}
      <div
        role="navigation"
        aria-label="Site navigation"
        aria-expanded={!isCollapsed}
        className={cn(
          "hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 shadow-xl border-r transition-all duration-300",
          isCollapsed ? "lg:w-16" : "lg:w-64",
          isDark ? "bg-slate-950 border-slate-800" : "bg-white border-gray-100"
        )}
      >
        <div className="flex flex-col flex-grow pt-4 overflow-y-auto overflow-x-hidden">
          <Logo />

          {/* Collapse toggle */}
          <div className={cn("pb-2", isCollapsed ? "flex justify-center px-2" : "flex px-4")}>
            <button
              onClick={toggleCollapsed}
              aria-label={isCollapsed ? "Expand sidebar navigation" : "Collapse sidebar to icons only"}
              title={isCollapsed ? "Expand sidebar" : "Collapse menu"}
              className={cn(
                "rounded-lg transition-all duration-200 flex items-center",
                isCollapsed
                  ? "h-8 w-8 justify-center"
                  : "h-8 w-full justify-start gap-2 px-2 text-xs font-medium",
                isDark
                  ? "text-slate-300 hover:text-white hover:bg-slate-800"
                  : "text-slate-600 hover:text-slate-900 hover:bg-blue-50"
              )}
            >
              {isCollapsed
                ? <Menu className="h-5 w-5" aria-hidden="true" />
                : <><ChevronLeft className="h-4 w-4" aria-hidden="true" /><span>Collapse menu</span></>}
            </button>
          </div>

          <Navigation collapsed={isCollapsed} />
          <div className="flex-1" />
          <QuickQuizCTA collapsed={isCollapsed} />
          {!isCollapsed && <UserProfileCard />}
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-2 pb-4 px-2">
              <Link to="/profile" title="View profile">
                <Button variant="ghost" size="icon" aria-label="View profile"
                  className={cn("h-10 w-10 rounded-xl", isDark ? "text-slate-300 hover:bg-slate-800" : "text-gray-700 hover:bg-gray-100")}>
                  <User className="h-5 w-5" aria-hidden="true" />
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Log out"
                className={cn("h-10 w-10 rounded-xl", isDark ? "text-slate-300 hover:bg-slate-800" : "text-gray-700 hover:bg-gray-100")}>
                <LogOut className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Button variant="ghost" size="icon"
                onClick={() => setTheme(isDark ? "light" : "dark")}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                className={cn("h-10 w-10 rounded-xl", isDark ? "text-slate-300 hover:bg-slate-800" : "text-gray-700 hover:bg-gray-100")}>
                {isDark ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
              </Button>
            </div>
          ) : (
            <ThemeToggleCard />
          )}
        </div>
      </div>

      {/* Mobile Header */}
      <div 
        className={[
          "sticky top-0 z-40 flex items-center h-20 lg:hidden shadow-md",
          isDark ? "bg-slate-900" : "bg-gray-100"
        ].join(" ")}
      >
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open navigation menu"
              className={[
                "h-16 w-16 px-4 border-0",
                isDark
                  ? "text-slate-100 hover:bg-slate-800/30"
                  : "text-gray-900 hover:bg-white/30",
              ].join(" ")}
            >
              <Menu className="h-7 w-7" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className={[
              "w-80 p-0 shadow-xl border-r",
              isDark ? "bg-slate-950 border-slate-800" : "bg-white border-gray-100",
            ].join(" ")}
          >
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden">
              <Logo />
              <Navigation collapsed={false} />
              <div className="flex-1" />
              <QuickQuizCTA collapsed={false} />
              <UserProfileCard />
              <ThemeToggleCard />
            </div>
          </SheetContent>
        </Sheet>
        
        {/* Page Title */}
        <h1 
          className={[
            "text-lg font-semibold ml-2",
            isDark ? "text-slate-100" : "text-gray-900"
          ].join(" ")}
        >
          {getPageTitle(location.pathname)}
        </h1>
      </div>

      {/* Main Content */}
      <div className={cn(
        "flex flex-col flex-1 transition-all duration-300",
        isCollapsed ? "lg:pl-16" : "lg:pl-64"
      )}>
        <main id="main-content" className="flex-1 min-h-screen">
          {children}
        </main>
      </div>
    </>
  );
}
